/**
 * SVG statique d'un repère orthogonal pour l'impression (quadrant 1 par défaut,
 * 4 quadrants si xMin/yMin négatifs — axes toujours par l'origine).
 *
 * Version print de `Repere2DInteractiveEleve` — uniquement la géométrie, pas
 * d'état React, pas de thème dark. Couleurs fixes encre (ink-700/ink-800),
 * quadrillage léger pour que l'élève place ses points au crayon.
 *
 * Props :
 *   - repere  : { xMin?, yMin?, xMax, yMax, xStep, yStep, xLabel?, yLabel?, grille?, quadrillage? }
 *   - points? : [{ x, y, label?, color? }] — corrigé (vide → repère à compléter)
 *   - dejaPlaces? : [{ x, y, label? }] — points statiques de l'énoncé (gris)
 *   - decor? : { polygones?, fleches? } — la figure et la flèche de l'énoncé (contenu.decor)
 *   - relier? : [labels] — les points du corrigé reliés dans l'ordre (contenu.relier)
 * repere.quadrillage (2026-09-24) : carreaux carrés, ni axes ni graduations
 * (utils/repereDecor.js), comme à l'écran.
 */

import { planEtiquettes } from '../../../utils/repereEtiquettes';
import {
    cadreRepere, decorSegments, segmentsRelies, flechePx, directionsDecor, directionsPolygone, etiquetteDirigee,
} from '../../../utils/repereDecor';

const LINE_COLOR = '#334155';   // ink-700
const TEXT_COLOR = '#1e293b';   // ink-800
const GRID_COLOR = '#cbd5e1';   // ink-300 (léger mais imprimable)
const STATIC_COLOR = '#64748b'; // ink-500
const FLECHE_COLOR = '#15803d'; // vert foncé, gris lisible en noir et blanc
const MAX_GRADUATIONS = 201;

const formatValue = (val) => {
    if (Number.isInteger(val)) return String(val);
    return Number(val.toFixed(4)).toString().replace('.', ',');
};

const getGraduations = (min, max, step) => {
    if (step <= 0 || max <= min) return [];
    const grads = [];
    for (let v = min; v <= max + step / 2 && grads.length <= MAX_GRADUATIONS; v += step) {
        grads.push(Math.round(v * 1e9) / 1e9);
    }
    return grads;
};

const PrintRepere2D = ({ repere, points = [], dejaPlaces = [], decor = null, relier = null }) => {
    const {
        xMin = 0, yMin = 0, xMax = 10, yMax = 10, xStep = 1, yStep = 1,
        xLabel = '', yLabel = '', grille = true,
    } = repere || {};

    if (!(xMax > xMin) || !(yMax > yMin) || !(xStep > 0) || !(yStep > 0)) return null;

    // Mêmes conventions géométriques que Repere2DInteractiveEleve (utils/repereDecor)
    const { W, H, padL, padT, plotW, plotH, quadrillage: quad } = cadreRepere(repere);
    const toPx = (x, y) => ({
        px: padL + ((x - xMin) / (xMax - xMin)) * plotW,
        py: padT + plotH - ((y - yMin) / (yMax - yMin)) * plotH,
    });

    const xGrads = getGraduations(xMin, xMax, xStep);
    const yGrads = getGraduations(yMin, yMax, yStep);
    const xEtiq = planEtiquettes(xGrads, xStep, xMin, xMax, plotW / (xMax - xMin), 22);
    const yEtiq = planEtiquettes(yGrads, yStep, yMin, yMax, plotH / (yMax - yMin), 14);
    const origin = toPx(0, 0);
    // Un point du décor ou un sommet de la figure du corrigé : son nom vers l'extérieur (2026-09-24)
    const dirsDecor = directionsDecor(decor, dejaPlaces);
    const dirsRelier = directionsPolygone(relier, Object.fromEntries(points.filter((p) => p.label != null).map((p) => [p.label, p])));
    const etiquette = (px, py, p, fill, d = null) => (d ? (() => {
        const e = etiquetteDirigee(px, py, d, 12, 13);
        return <text x={e.x} y={e.y} textAnchor={e.ancre} fill={fill} style={{ fontSize: 13, fontWeight: 700 }}>{p.label}</text>;
    })() : p.y === 0 ? (
        <text x={px} y={py - 9} textAnchor="middle" fill={fill} style={{ fontSize: 13, fontWeight: 700 }}>{p.label}</text>
    ) : (
        <text x={px + 8} y={py + 4.5} textAnchor="start" fill={fill} style={{ fontSize: 13, fontWeight: 700 }}>{p.label}</text>
    ));
    const ligne = (c, key, props) => {
        const a = toPx(c.x1, c.y1); const b = toPx(c.x2, c.y2);
        return <line key={key} x1={a.px} y1={a.py} x2={b.px} y2={b.py} strokeLinecap="round" {...props} />;
    };
    const { cotes, fleches } = decorSegments(decor, dejaPlaces);
    const places = {};
    points.forEach((p) => { if (p.label != null) places[p.label] = p; });
    const relies = segmentsRelies(relier, places);

    return (
        <svg
            className="ep-repere2d"
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            style={{ '--ep-svg-w': `${W}px` }}
        >
            {/* Quadrillage (la ligne 0 est portée par les axes, sauf en quadrillage sans axes) */}
            {grille && xGrads.map((v, i) => (quad || v !== 0) && (
                <line key={`gx-${i}`} x1={toPx(v, 0).px} y1={padT} x2={toPx(v, 0).px} y2={padT + plotH} stroke={GRID_COLOR} strokeWidth={0.7} />
            ))}
            {grille && yGrads.map((v, i) => (quad || v !== 0) && (
                <line key={`gy-${i}`} x1={padL} y1={toPx(0, v).py} x2={padL + plotW} y2={toPx(0, v).py} stroke={GRID_COLOR} strokeWidth={0.7} />
            ))}

            {quad ? null : (<>
                {/* Axes + flèches (toujours par l'origine — xMin/yMin ≤ 0) */}
                <line x1={padL} y1={origin.py} x2={padL + plotW + 12} y2={origin.py} stroke={LINE_COLOR} strokeWidth={1.5} />
                <polygon points={`${padL + plotW + 18},${origin.py} ${padL + plotW + 9},${origin.py - 4} ${padL + plotW + 9},${origin.py + 4}`} fill={LINE_COLOR} />
                <line x1={origin.px} y1={padT + plotH} x2={origin.px} y2={padT - 12} stroke={LINE_COLOR} strokeWidth={1.5} />
                <polygon points={`${origin.px},${padT - 18} ${origin.px - 4},${padT - 9} ${origin.px + 4},${padT - 9}`} fill={LINE_COLOR} />

                {/* Graduations + valeurs (le 0 décalé à gauche de l'axe des ordonnées) */}
                {xGrads.map((v, i) => {
                    const { px } = toPx(v, 0);
                    const estOrigine = v === 0 && yMin < 0;
                    return (
                        <g key={`tx-${i}`}>
                            <line x1={px} y1={origin.py - 4} x2={px} y2={origin.py + 4} stroke={LINE_COLOR} strokeWidth={1} />
                            {xEtiq[i] > 0 && (
                                <text
                                    x={estOrigine ? px - 6 : px}
                                    y={origin.py + 18}
                                    textAnchor={estOrigine ? 'end' : 'middle'}
                                    fill={TEXT_COLOR} style={{ fontSize: 12 * xEtiq[i] }}
                                >
                                    {formatValue(v)}
                                </text>
                            )}
                        </g>
                    );
                })}
                {yGrads.map((v, i) => {
                    if (v === 0) return null; // le 0 est porté par l'axe des abscisses
                    const { py } = toPx(0, v);
                    return (
                        <g key={`ty-${i}`}>
                            <line x1={origin.px - 4} y1={py} x2={origin.px + 4} y2={py} stroke={LINE_COLOR} strokeWidth={1} />
                            {yEtiq[i] > 0 && (
                                <text x={origin.px - 8} y={py + 3.5} textAnchor="end" fill={TEXT_COLOR} style={{ fontSize: 12 * yEtiq[i] }}>
                                    {formatValue(v)}
                                </text>
                            )}
                        </g>
                    );
                })}

                {/* Noms des axes */}
                {xLabel && (
                    <text x={padL + plotW + 16} y={origin.py + 32} textAnchor="end" fill={TEXT_COLOR} style={{ fontSize: 13, fontWeight: 600 }}>
                        {xLabel}
                    </text>
                )}
                {yLabel && (
                    <text x={origin.px + 8} y={padT - 10} textAnchor="start" fill={TEXT_COLOR} style={{ fontSize: 13, fontWeight: 600 }}>
                        {yLabel}
                    </text>
                )}
            </>)}

            {/* Décor : la figure et la flèche de l'énoncé ; la figure du corrigé, points reliés */}
            {cotes.map((c, i) => ligne(c, `dc-${i}`, { stroke: LINE_COLOR, strokeWidth: 1.8 }))}
            {fleches.map((f, i) => {
                const a = toPx(f.x1, f.y1); const b = toPx(f.x2, f.y2);
                const fl = flechePx(a.px, a.py, b.px, b.py, { retrait: 5 });
                return (
                    <g key={`df-${i}`}>
                        <line x1={fl.trait.x1} y1={fl.trait.y1} x2={fl.trait.x2} y2={fl.trait.y2} stroke={FLECHE_COLOR} strokeWidth={2.4} strokeLinecap="round" />
                        <polygon points={fl.pointe} fill={FLECHE_COLOR} />
                    </g>
                );
            })}
            {relies.map((c, i) => ligne(c, `rl-${i}`, { stroke: TEXT_COLOR, strokeWidth: 1.8 }))}

            {/* Étiquette d'un point : à droite du point, sauf sur l'axe horizontal où elle reste au-dessus
                (même règle qu'à l'écran : un point sur l'axe vertical n'a plus son nom sur le trait, 2026-09-11) */}
            {/* Points statiques de l'énoncé (gris) */}
            {dejaPlaces.map((p, i) => {
                const { px, py } = toPx(p.x, p.y);
                return (
                    <g key={`static-${i}`}>
                        <circle cx={px} cy={py} r={4} fill={STATIC_COLOR} stroke="white" strokeWidth={1} />
                        {p.label && etiquette(px, py, p, STATIC_COLOR, dirsDecor[p.label])}
                    </g>
                );
            })}

            {/* Points du corrigé (croix + guides pointillés vers les axes, sauf en quadrillage) */}
            {points.map((p, i) => {
                const { px, py } = toPx(p.x, p.y);
                const color = p.color || TEXT_COLOR;
                return (
                    <g key={`pt-${i}`}>
                        {quad ? null : (<>
                            <line x1={px} y1={py} x2={px} y2={origin.py} stroke={color} strokeWidth={0.8} strokeDasharray="3 3" opacity={0.6} />
                            <line x1={px} y1={py} x2={origin.px} y2={py} stroke={color} strokeWidth={0.8} strokeDasharray="3 3" opacity={0.6} />
                        </>)}
                        <line x1={px - 4} y1={py - 4} x2={px + 4} y2={py + 4} stroke={color} strokeWidth={2} />
                        <line x1={px - 4} y1={py + 4} x2={px + 4} y2={py - 4} stroke={color} strokeWidth={2} />
                        {p.label && etiquette(px, py, p, color, dirsRelier[p.label])}
                    </g>
                );
            })}
        </svg>
    );
};

export default PrintRepere2D;
